import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injector, Service, signal } from '@angular/core';
import { createHttpContext } from '@app/core/http/http-context';
import { Response } from '@app/shared/services/local/Response';
import { ITenantService } from '@app/shared/services/itenant.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import {
  ACTIVATION_LINK_KIND_KEY,
  ACTIVATION_LINK_PREFETCH_TTL_MS,
  TENANT_ACTIVATION_URL,
} from './tenant-lifecycle.config';

/** `activate`: self-service checkout. `contact`: book a call with sales. */
export type ActivationLinkKind = 'activate' | 'contact';

export interface ActivationLink {
  kind: ActivationLinkKind;
  url: string;
}

/** `forbidden`: the user is not an admin of the workspace (403). `unavailable`: everything else. */
export type ActivationLinkFailure = 'forbidden' | 'unavailable';

export class ActivationLinkError extends Error {
  constructor(readonly failure: ActivationLinkFailure) {
    super(`activation_link_${failure}`);
    this.name = 'ActivationLinkError';
  }
}

const TOAST_KEYS: Record<ActivationLinkFailure, string> = {
  forbidden: 'TenantLifecycle.ActivationForbidden',
  unavailable: 'TenantLifecycle.ActivationUnavailable',
};

/** Used when no translation is loaded (unit tests, a failed bundle). */
const TOAST_FALLBACKS: Record<ActivationLinkFailure, string> = {
  forbidden: 'Only company admins of your workspace can activate the plan.',
  unavailable: 'Activation is not available right now. Please try again later or write to support@hypecast.one.',
};

/**
 * Whether the tenant API can be asked for the activation link at all. Without
 * an API URL the banner falls back to the static pricing page.
 */
export function activationApiAvailable(
  environmentName: string,
  serverUrl: string | null | undefined,
  tenantApiUrl: string | null | undefined,
): boolean {
  return environmentName === 'DEV' ? !!serverUrl : !!tenantApiUrl;
}

/**
 * Reads `{ kind, url }` from the API envelope. Only the two known kinds and
 * absolute http(s) or mailto links are accepted; anything else counts as unavailable.
 */
export function parseActivationLink(data: unknown): ActivationLink | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const { kind, url } = data as { kind?: unknown; url?: unknown };
  if ((kind !== 'activate' && kind !== 'contact') || typeof url !== 'string') {
    return null;
  }
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'https:' || protocol === 'http:' || protocol === 'mailto:' ? { kind, url } : null;
  } catch {
    return null;
  }
}

/**
 * The trial banner's "Plan aktivieren" button: asks the tenant API
 * (GET api/v1/tenant/activation-link) where the activation of this workspace
 * happens and opens it in a new tab.
 *
 * A link may be a single-use checkout session, so a URL is used at most once.
 * Only its kind (activate or contact) is remembered for the tab session, so
 * the button label is right from the start.
 */
@Service()
export class ActivationLinkService {
  private readonly http = inject(HttpClient);
  private readonly tenantService = inject(ITenantService, { optional: true });
  // Resolved lazily: tests and early callers may run without toast/i18n providers.
  private readonly injector = inject(Injector);

  private readonly kindState = signal<ActivationLinkKind>(readStoredKind());
  private prefetched: { link: ActivationLink; at: number } | null = null;
  private prefetchStarted = false;

  /** Kind of the last link the API sent (activate until it says otherwise). */
  readonly kind = this.kindState.asReadonly();

  /** Translation key for the button label. */
  readonly labelKey = computed(() =>
    this.kindState() === 'contact' ? 'TenantLifecycle.Contact' : 'TenantLifecycle.Activate',
  );

  /** Test seam for the clock that decides whether a prefetched link is still fresh. */
  now: () => number = () => Date.now();

  /**
   * Fetches the link once per tab session in the background, so the label
   * matches the kind before the first click. Failures stay silent; the click
   * asks again and reports them.
   */
  prefetch(): void {
    if (this.prefetchStarted || readStoredKind(null) !== null) {
      return;
    }
    this.prefetchStarted = true;
    this.fetch()
      .then((link) => {
        this.prefetched = { link, at: this.now() };
      })
      .catch((): void => undefined);
  }

  /** The link to open now. A prefetched link is handed out once, and only while it is fresh. */
  async take(): Promise<ActivationLink> {
    const prefetched = this.prefetched;
    this.prefetched = null;
    if (prefetched !== null && this.now() - prefetched.at < ACTIVATION_LINK_PREFETCH_TTL_MS) {
      return prefetched.link;
    }
    return this.fetch();
  }

  /**
   * Click handler of the activation button. Opens the tab synchronously (a
   * window opened after an await counts as a popup), then points it at the
   * link. On failure the tab closes again and a toast says why.
   */
  async open(): Promise<void> {
    const tab = openBlankTab();

    let link: ActivationLink;
    try {
      link = await this.take();
    } catch (error: unknown) {
      tab?.close();
      this.notify(error instanceof ActivationLinkError ? error.failure : 'unavailable');
      return;
    }

    followLink(tab, link.url);
  }

  /**
   * Opens a known link (the contact link of the activation modal) the same
   * way: a new tab without opener, a mail link in place. Call it from a click.
   */
  openUrl(url: string): void {
    followLink(url.startsWith('mailto:') ? null : openBlankTab(), url);
  }

  /** Remembers the kind the in-app activation reported, so the button label matches it. */
  rememberKind(kind: ActivationLinkKind): void {
    this.kindState.set(kind);
    writeStoredKind(kind);
  }

  /** Toast for a failed activation, e.g. "Nur Company Admins ... können den Plan aktivieren." */
  notify(failure: ActivationLinkFailure): void {
    const translate = this.injector.get(TranslateService, null);
    const translated: unknown = translate?.instant(TOAST_KEYS[failure]);
    const text =
      typeof translated === 'string' && translated !== TOAST_KEYS[failure] ? translated : TOAST_FALLBACKS[failure];
    this.injector.get(ToastrService, null)?.warning(text);
  }

  private async fetch(): Promise<ActivationLink> {
    const tenant = this.tenantService?.tenantSignal() ?? null;
    if (!activationApiAvailable(environment.environmentName, environment.serverUrl, tenant?.apiUrl)) {
      return { kind: 'activate', url: TENANT_ACTIVATION_URL };
    }

    let response: Response<unknown>;
    try {
      response = await firstValueFrom(
        this.http.get<Response<unknown>>('tenant/activation-link', {
          context: createHttpContext({ skipErrorHandler: true }),
        }),
      );
    } catch (error: unknown) {
      throw new ActivationLinkError(
        error instanceof HttpErrorResponse && error.status === 403 ? 'forbidden' : 'unavailable',
      );
    }

    const link = parseActivationLink(response?.data);
    if (link === null) {
      throw new ActivationLinkError('unavailable');
    }
    this.rememberKind(link.kind);
    return link;
  }
}

/** Opened synchronously in the click: a window opened after an await counts as a popup. */
function openBlankTab(): Window | null {
  const tab = window.open('', '_blank');
  if (tab !== null) {
    // Same effect as rel="noopener"; passing it as a feature would make window.open return null.
    tab.opener = null;
  }
  return tab;
}

function followLink(tab: Window | null, url: string): void {
  if (url.startsWith('mailto:')) {
    // A mail link would leave an empty tab behind; the mail client opens from this one.
    tab?.close();
    window.location.assign(url);
    return;
  }
  if (tab !== null) {
    tab.location.href = url;
    return;
  }
  // Popups blocked entirely: go there in this tab rather than doing nothing.
  window.location.assign(url);
}

function readStoredKind(): ActivationLinkKind;
function readStoredKind(fallback: null): ActivationLinkKind | null;
function readStoredKind(fallback: ActivationLinkKind | null = 'activate'): ActivationLinkKind | null {
  try {
    const stored = sessionStorage.getItem(ACTIVATION_LINK_KIND_KEY);
    return stored === 'activate' || stored === 'contact' ? stored : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredKind(kind: ActivationLinkKind): void {
  try {
    sessionStorage.setItem(ACTIVATION_LINK_KIND_KEY, kind);
  } catch {
    // Storage can be blocked; the label then stays right for this page only.
  }
}
